import { useContext, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import * as yup from "yup";

import MyButton from "../../components/MyButton";
import MyTextField from "../../components/inputs/MyTextField";
import MyTextArea from "../../components/inputs/MyTextArea";
import { useBoardItemComment } from "../../hooks/useBoardItemComment";
import { SiteContext } from "../../context/SiteProvider";
import MyInputCaptcha from "../../components/inputs/MyInputCaptcha";
import { AuthContext } from "../../context/AuthProvider";

const CommentForm = ({
  board,
  selectComment,
  boardItem,
  setSelectComment = () => {},
}) => {
  const { createBoardItemCommentMutation, updateBoardItemCommentMutation } =
    useBoardItemComment();
  const queryClient = useQueryClient();
  const { setLoadingScreen } = useContext(SiteContext);
  const { isAuthenticated, profile } = useContext(AuthContext);

  const privateViewToken = new URLSearchParams(window.location.search).get(
    "privateViewToken"
  );

  const initValues = {
    writerName: "",
    writerPassword: "",
    comment: "",
    captchaToken: "",
    isRequiredCaptcha: false,
  };

  const validationSchema = yup.object({
    captcha: yup.string().when("isRequiredCaptcha", {
      is: true,
      then: () =>
        yup.string().required("자동입력방지 인증코드를 입력해주세요."),
    }),
    writerName: yup.string().when("isAuthenticated", {
      is: false,
      then: () => yup.string().required("필수 입력 항목입니다."),
      otherwise: () => yup.string().notRequired(),
    }),
    writerPassword: yup.string().when("isAuthenticated", {
      is: false,
      then: () => yup.string().required("필수 입력 항목입니다."),
      otherwise: () => yup.string().notRequired(),
    }),
    comment: yup.string().required("필수 입력 항목입니다."),
  });

  const methods = useForm({
    defaultValues: initValues,
    resolver: yupResolver(validationSchema),
  });

  const showCaptcha = useMemo(
    () => !!board?.options?.useCaptchaInComment,
    [board]
  );

  const onCancel = () => {
    methods.reset();
    setSelectComment(null);
  };

  const onSubmit = async (vals) => {
    if (board.boardCode) {
      const payload = {
        comment: vals.comment,
        isPrivate: false,
      };

      if (showCaptcha) {
        if (!vals.captchaToken) {
          methods.setError("captcha", {
            message: "인증을 진행해주세요.",
          });
          return;
        }
        payload.captchaToken = vals.captchaToken;
      }

      if (!selectComment?.commentNo) {
        if (profile?.userId) {
          payload.userId = profile.userId;
        } else {
          payload.writerName = vals.writerName;
          payload.writerPassword = vals.writerPassword;
        }
      } else {
        if (selectComment?.writer?.userId) {
          payload.userId = selectComment.writer.userId;
        } else {
          payload.writerName = selectComment.writer.writerName;
          if (!profile?.userId) payload.writerPassword = vals.writerPassword;
        }
      }

      if (!isAuthenticated && selectComment?.anonymousEditToken)
        payload.anonymousEditToken = selectComment.anonymousEditToken;

      setLoadingScreen(true);

      if (!selectComment?.commentNo) {
        createBoardItemCommentMutation.mutate(
          {
            boardCode: board?.boardCode,
            itemNo: boardItem?.itemNo,
            payload,
          },
          {
            onSuccess: (res) => {
              if (res) {
                alert("댓글이 등록되었습니다.");
                queryClient.invalidateQueries({
                  queryKey: [
                    "/board-item-comment?boardCode={boardCode}&itemNo={itemNo}&page=1",
                  ],
                });
                methods.reset();
              } else {
                alert("댓글 업로드 중 오류가 발생했습니다.");
              }
            },
            onError: () => {
              alert("댓글 업로드 중 오류가 발생했습니다.");
            },
            onSettled: () => {
              setLoadingScreen(false);
              if (showCaptcha) {
                methods.clearErrors("captcha");
                methods.setValue("captcha", "");
                methods.setValue("captchaToken", "");
                queryClient.invalidateQueries({
                  queryKey: ["/site-common/captcha/generate"],
                });
              }
            },
          }
        );
      } else {
        updateBoardItemCommentMutation.mutate(
          {
            boardCode: board?.boardCode,
            commentNo: selectComment.commentNo,
            itemNo: boardItem?.itemNo,
            payload,
            rawPrivateViewToken: privateViewToken,
            privateViewToken: selectComment?.privateViewToken,
          },
          {
            onSuccess: (res) => {
              if (res) {
                alert("댓글이 수정되었습니다.");
                queryClient.invalidateQueries({
                  queryKey: [
                    "/board-item-comment?boardCode={boardCode}&itemNo={itemNo}&page=1",
                  ],
                });
                methods.reset();
                setSelectComment(null);
              } else {
                alert("댓글 업로드 중 오류가 발생했습니다.");
              }
            },
            onError: () => {
              alert("댓글 업로드 중 오류가 발생했습니다.");
            },
            onSettled: () => {
              setLoadingScreen(false);
              if (showCaptcha) {
                methods.clearErrors("captcha");
                methods.setValue("captcha", "");
                methods.setValue("captchaToken", "");
                queryClient.invalidateQueries({
                  queryKey: ["/site-common/captcha/generate"],
                });
              }
            },
          }
        );
      }
    } else {
      alert("게시글 카테고리가 존재하지 않습니다.");
    }
  };

  useEffect(() => {
    methods.setValue("isRequiredCaptcha", showCaptcha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCaptcha]);

  useEffect(() => {
    methods.setValue("isAuthenticated", isAuthenticated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (selectComment?.commentNo) {
      methods.setValue("comment", selectComment.comment);
      methods.setValue("writerName", selectComment.writer.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectComment]);

  return (
    <div>
      {!selectComment?.commentNo && (
        <h2 className="font-semibold lg:text-3xl md:text-2xl text-xl mb-4">
          댓글 작성
        </h2>
      )}

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          {!isAuthenticated && (
            <div className="flex flex-wrap gap-x-5 gap-y-3 md:mb-5 mb-3">
              <div className="flex flex-wrap lg:w-[40%] w-full md:min-w-[250px]">
                <label
                  htmlFor="writerName"
                  className="md:mt-4 md:w-[150px] w-full font-semibold"
                >
                  작성자 <span className="text-error">*</span>
                </label>
                <div className="flex-1">
                  <MyTextField
                    id="writerName"
                    name="writerName"
                    placeholder="작성자 이름을 입력해주세요."
                  />
                </div>
              </div>
              <div className="flex flex-wrap lg:w-[40%] w-full md:min-w-[250px]">
                <label
                  htmlFor="writerPassword"
                  className="md:mt-4 md:w-[150px] w-full font-semibold"
                >
                  비밀번호 <span className="text-error">*</span>
                </label>
                <div className="flex-1">
                  <MyTextField
                    id="writerPassword"
                    type="password"
                    name="writerPassword"
                    placeholder="비밀번호를 입력하세요."
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap md:mb-5 mb-3">
            <label
              htmlFor="comment"
              className="md:mt-4 md:w-[150px] w-full font-semibold"
            >
              댓글내용 <span className="text-error">*</span>
            </label>
            <div className="flex-1">
              <MyTextArea
                rows="5"
                id="comment"
                name="comment"
                placeholder="댓글내용을 입력해주세요."
              />
            </div>
          </div>

          {showCaptcha && (
            <MyInputCaptcha
              name="captchaToken"
              labelClass="md:w-[150px]"
              className={isAuthenticated ? "hidden" : ""}
            />
          )}

          <div className="flex items-center sm:gap-4 gap-2 md:mt-10 mt-5">
            <MyButton
              variant="outlined"
              className="font-semibold ml-auto min-w-[120px]"
              onClick={onCancel}
            >
              <span className="text-gray-dark">취소</span>
            </MyButton>

            <MyButton
              variant="contained"
              color="primary"
              className="font-semibold min-w-[120px]"
              type="submit"
            >
              등록
            </MyButton>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default CommentForm;
